import { useEffect } from "react";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { format } from "date-fns";
import { CalendarIcon, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { ProfessionalCombobox } from "@/components/common/professional-combobox";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";

interface PrescriptionFilters {
  status: string;
  document_type: string;
  professional_id: string;
  start_date: string;
  end_date: string;
}

interface PrescriptionFilterFormValues {
  status: string;
  document_type: string;
  professional_id: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

interface PrescriptionFilterProps {
  onApplyFilters: (filters: PrescriptionFilters) => void;
  currentFilters: PrescriptionFilters;
}

export default function PrescriptionFilter({
  onApplyFilters,
  currentFilters,
}: PrescriptionFilterProps) {
  const parseDate = (value?: string) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const form = useForm<PrescriptionFilterFormValues>({
    defaultValues: {
      status: currentFilters.status,
      document_type: currentFilters.document_type,
      professional_id: currentFilters.professional_id,
      dateRange: {
        start: parseDate(currentFilters.start_date),
        end: parseDate(currentFilters.end_date),
      },
    },
  });

  const { data: professionalsData, isLoading: professionalsLoading } =
    useAllProfessionals();

  const professionals = professionalsData?.professionals || [];

  const getDateFilterValues = (
    dateRange: PrescriptionFilterFormValues["dateRange"],
  ) => {
    if (dateRange.start && dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      return {
        start_date: dateRange.start.toISOString(),
        end_date: endDate.toISOString(),
      };
    }
    return {
      start_date: "",
      end_date: "",
    };
  };

  const handleStatusChange = (newStatus: string) => {
    const currentValues = form.getValues();
    onApplyFilters({
      status: newStatus,
      document_type: currentValues.document_type,
      professional_id: currentValues.professional_id,
      ...getDateFilterValues(currentValues.dateRange),
    });
  };

  const handleDocumentTypeChange = (newDocumentType: string) => {
    const currentValues = form.getValues();
    onApplyFilters({
      status: currentValues.status,
      document_type: newDocumentType,
      professional_id: currentValues.professional_id,
      ...getDateFilterValues(currentValues.dateRange),
    });
  };

  const handleDoctorChange = (newProfessionalId: string) => {
    const currentValues = form.getValues();
    onApplyFilters({
      status: currentValues.status,
      document_type: currentValues.document_type,
      professional_id: newProfessionalId,
      ...getDateFilterValues(currentValues.dateRange),
    });
  };

  const handleDateRangeChange = (
    newDateRange: PrescriptionFilterFormValues["dateRange"],
  ) => {
    const { status, document_type, professional_id } = form.getValues();
    onApplyFilters({
      status,
      document_type,
      professional_id,
      ...getDateFilterValues(newDateRange),
    });
  };

  useEffect(() => {
    form.reset({
      status: currentFilters.status,
      document_type: currentFilters.document_type,
      professional_id: currentFilters.professional_id,
      dateRange: {
        start: parseDate(currentFilters.start_date),
        end: parseDate(currentFilters.end_date),
      },
    });
  }, [currentFilters, form]);

  return (
    <Form {...form}>
      <form>
        <div className="flex flex-wrap gap-3 items-center">
          {/* Date Range Filter — leftmost, same position as the Sessions tab */}
          <FormField
            control={form.control}
            name="dateRange"
            render={({ field }) => (
              <FormItem>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={`w-[240px] justify-start text-left font-normal ${
                          !field.value.start && "text-gray-500"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value.start && field.value.end ? (
                          <>
                            {format(field.value.start, "LLL dd, y")} -{" "}
                            {format(field.value.end, "LLL dd, y")}
                          </>
                        ) : (
                          <span>Pick a Date Range</span>
                        )}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-4" align="start">
                    <div className="flex items-end gap-3 mb-4 px-4">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1.5 block">
                          Start
                        </label>
                        <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                          {field.value.start ? (
                            format(field.value.start, "LLL dd, y")
                          ) : (
                            <span className="text-muted-foreground">
                              Select
                            </span>
                          )}
                        </div>
                      </div>

                      <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />

                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1.5 block">
                          End
                        </label>
                        <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                          {field.value.end ? (
                            format(field.value.end, "LLL dd, y")
                          ) : (
                            <span className="text-muted-foreground">
                              Select
                            </span>
                          )}
                        </div>
                      </div>

                      {(field.value.start || field.value.end) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            const emptyRange = { start: null, end: null };
                            field.onChange(emptyRange);
                            handleDateRangeChange(emptyRange);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <Calendar
                      initialFocus
                      mode="range"
                      selected={{
                        from: field.value.start || undefined,
                        to: field.value.end || undefined,
                      }}
                      onSelect={(range) => {
                        const newDateRange = {
                          start: range?.from || null,
                          end: range?.to || null,
                        };
                        field.onChange(newDateRange);
                        if (range?.from && range?.to) {
                          handleDateRangeChange(newDateRange);
                        }
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />

          {/* Status Filter */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleStatusChange(value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="PROCESSED">Processed</SelectItem>
                    <SelectItem value="SENT">Sent</SelectItem>
                    <SelectItem value="PAID">Paid</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="UNMATCHED_DRUGS">Unmatched Drugs</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          {/* Document Type Filter */}
          <FormField
            control={form.control}
            name="document_type"
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleDocumentTypeChange(value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Document Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Types</SelectItem>
                    <SelectItem value="Mental health">Mental Health</SelectItem>
                    <SelectItem value="Men's health">Men's Health</SelectItem>
                    <SelectItem value="Women's health">Women's Health</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          {/* Doctor Filter — shared searchable combobox */}
          <FormField
            control={form.control}
            name="professional_id"
            render={({ field }) => (
              <FormItem className="w-[220px]">
                <ProfessionalCombobox
                  professionals={professionals}
                  value={field.value}
                  disabled={professionalsLoading}
                  onSelect={(value) => {
                    field.onChange(value);
                    handleDoctorChange(value);
                  }}
                />
              </FormItem>
            )}
          />

        </div>
      </form>
    </Form>
  );
}
